/**
 * Modern Bold · <product-bundle> custom element
 * ─────────────────────────────────────────────────────────────────────────────
 * Renders the "Build your bundle" widget inline between the variant picker
 * and the qty/ATC row inside .mb-pdp-detail. Placed in the DOM by
 * snippets/modern-product-bundle.liquid; no standalone section needed.
 *
 * Why a custom element?
 *   Self-contained init, lazy-safe (connectedCallback fires whenever the
 *   element lands in the DOM regardless of script-load order), and aligns
 *   with <product-form> — the only other custom element on this surface.
 *
 * ─── CONFIG CONTRACT (embedded JSON) ────────────────────────────────────────
 *   <script type="application/json" data-bundle-config>
 *     {
 *       "productId":       number,
 *       "productPrice":    number,      cents — initial only; updated via event
 *       "currentVariantId": number,
 *       "variants":        [{id, title, option1, price, available}],
 *       "padProducts": {
 *         "mohawk":   { handle, variants: [{id, title, price, available}] },
 *         "standard": { handle, variants: [{id, title, price, available}] }
 *       },
 *       "modalId":   string,
 *       "sectionId": string
 *     }
 *   </script>
 *
 * ─── VARIANT PRICE REACTIVITY ────────────────────────────────────────────────
 *   Listens for `mb:variant-change` (dispatched by modern-product.js's
 *   variant picker on every selection). Payload shape: { variant: object|null }.
 *   The variant carries .price (cents) and .option1 (size) directly — no DOM
 *   scraping or URL parsing needed. On every change we update the base-price
 *   display, re-run pad size matching, and recalculate the running total.
 *
 * ─── EVENTS CONSUMED ─────────────────────────────────────────────────────────
 *   mb:variant-change          → re-sync variant price + pad size match
 *   popstate                   → re-sync from URL (browser back/forward)
 *
 * ─── PUBLIC API ──────────────────────────────────────────────────────────────
 *   getSelectedItems() → [{ id, quantity }, ...]
 *     Returns the currently-selected pad + addon items in the order
 *     {pad?, ...addons}. Empty array when nothing is checked. Consumed by
 *     <product-form>'s submit path so cart additions are atomic — single
 *     POST containing the main rug + all bundle items. The bundle no
 *     longer fires its own POST after `product-form:added` (would race
 *     and fail silently on 422); it's now a pure data source.
 *
 * No setInterval. No globals written. No var. ES2024 private fields.
 */
if (!customElements.get('product-bundle')) {

  class ProductBundle extends HTMLElement {
    // Private state
    #config = null;
    #allVariants = [];
    #mohawkVariants = [];
    #standardVariants = [];
    #currentVariantSize = '';
    #currentMohawkVariant = null;
    #currentStandardVariant = null;
    #currentProductPrice = 0;

    // Private DOM refs (set in #cacheDom)
    #announceEl = null;
    #basePriceEl = null;
    #mohawkRow = null;
    #standardRow = null;
    #mohawkPriceEl = null;
    #standardPriceEl = null;
    #addonInputs = [];
    #padRows = [];
    #totalEl = null;
    #modal = null;
    #learnMoreBtn = null;
    #lastFocusTrigger = null;

    // Bound handlers (stored so removeEventListener works)
    #onStateChange = null;
    #onPopState = null;
    #onKeydown = null;
    #onModalKeydown = null;

    static #formatMoney(cents) {
      if (!Number.isFinite(cents)) return '--';
      const dollars = cents / 100;
      return dollars % 1 === 0
        ? '$' + dollars.toFixed(0)
        : '$' + dollars.toFixed(2);
    }

    static #announce(region, message) {
      if (!region || region.textContent === message) return;
      region.textContent = message;
    }

    static #normalizeSize(str) {
      if (!str) return '';
      return str
        .toLowerCase()
        .replace(/['"″′]/g, '')
        .replace(/\s+/g, '')
        .replace(/×/g, 'x');
    }

    static #extractDimensions(str) {
      if (!str) return null;
      const lower = str.toLowerCase();
      let shape = 'rectangle';
      if (lower.includes('round')) shape = 'round';
      else if (lower.includes('oval')) shape = 'oval';
      else if (lower.includes('square')) shape = 'square';
      else if (lower.includes('runner')) shape = 'runner';

      const nums = str.match(/(\d+(?:\.\d+)?)'?\s*"?(\d+)?/g);
      if (!nums || !nums.length) return null;

      const parseDim = (s) => {
        const m = s.match(/(\d+(?:\.\d+)?)'?\s*"?(\d+)?/);
        if (!m) return 0;
        return (parseFloat(m[1]) || 0) + (parseInt(m[2], 10) || 0) / 12;
      };

      const w = parseDim(nums[0]);
      const h = nums.length > 1 ? parseDim(nums[1]) : w;
      if (w === 0) return null;
      return { w, h, area: w * h, shape };
    }

    static #findMatchingVariant(variants, rugSize) {
      if (!variants?.length || !rugSize) return null;

      const normalizedRug = ProductBundle.#normalizeSize(rugSize);

      for (const v of variants) {
        if (v.available && ProductBundle.#normalizeSize(v.title) === normalizedRug) return v;
      }

      const rugDims = ProductBundle.#extractDimensions(rugSize);
      if (!rugDims) {
        const available = variants.filter((v) => v.available);
        if (!available.length) return null;
        return available.reduce((best, v) => {
          const d = ProductBundle.#extractDimensions(v.title);
          const bd = ProductBundle.#extractDimensions(best.title);
          return d && bd && d.area > bd.area ? v : best;
        }, available[0]);
      }

      let bestMatch = null;
      let bestArea = Infinity;
      for (const pv of variants) {
        if (!pv.available) continue;
        const pd = ProductBundle.#extractDimensions(pv.title);
        if (!pd) continue;
        if (pd.shape === rugDims.shape && pd.area >= rugDims.area && pd.area < bestArea) {
          bestArea = pd.area;
          bestMatch = pv;
        }
      }
      if (bestMatch) return bestMatch;

      if (rugDims.shape === 'rectangle' || rugDims.shape === 'runner') {
        bestArea = Infinity;
        for (const rv of variants) {
          if (!rv.available) continue;
          const rd = ProductBundle.#extractDimensions(rv.title);
          if (!rd) continue;
          if (
            (rd.shape === 'rectangle' || rd.shape === 'runner') &&
            rd.area >= rugDims.area &&
            rd.area < bestArea
          ) {
            bestArea = rd.area;
            bestMatch = rv;
          }
        }
      }
      if (bestMatch) return bestMatch;

      bestArea = Infinity;
      for (const av of variants) {
        if (!av.available) continue;
        const ad = ProductBundle.#extractDimensions(av.title);
        if (!ad) continue;
        if (ad.area >= rugDims.area && ad.area < bestArea) {
          bestArea = ad.area;
          bestMatch = av;
        }
      }
      if (bestMatch) return bestMatch;

      const allAvail = variants.filter((v) => v.available);
      if (!allAvail.length) return null;
      return allAvail.reduce((best, v) => {
        const d = ProductBundle.#extractDimensions(v.title);
        const bd = ProductBundle.#extractDimensions(best.title);
        return d && bd && d.area > bd.area ? v : best;
      }, allAvail[0]);
    }

    connectedCallback() {
      this.#config = this.#parseConfig();
      if (!this.#config) return;

      this.#allVariants     = this.#config.variants || [];
      this.#mohawkVariants  = this.#config.padProducts?.mohawk?.variants || [];
      this.#standardVariants = this.#config.padProducts?.standard?.variants || [];
      this.#currentProductPrice = this.#config.productPrice || 0;

      this.#cacheDom();
      this.#cachePadVariants();
      this.#bindEvents();
      this.#syncFromVariant(this.#currentVariantId());
    }

    disconnectedCallback() {
      this.#unbindEvents();
    }

    // ── Config ─────────────────────────────────────────────────────────────

    #parseConfig() {
      const el = this.querySelector('[data-bundle-config]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        console.warn('[product-bundle] Failed to parse bundle config:', e);
        return null;
      }
    }

    #currentVariantId() {
      const params = new URLSearchParams(window.location.search);
      return params.get('variant') || String(this.#config.currentVariantId || '');
    }

    // ── DOM ────────────────────────────────────────────────────────────────

    #cacheDom() {
      this.#announceEl     = this.querySelector('[data-bundle-announce]');
      this.#basePriceEl    = this.querySelector('[data-bundle-base-price]');
      this.#mohawkRow      = this.querySelector('[data-bundle-pad-type="mohawk"]');
      this.#standardRow    = this.querySelector('[data-bundle-pad-type="standard"]');
      this.#mohawkPriceEl  = this.querySelector('[data-bundle-pad-price="mohawk"]');
      this.#standardPriceEl = this.querySelector('[data-bundle-pad-price="standard"]');
      this.#addonInputs    = Array.from(this.querySelectorAll('[data-bundle-addon-id]'));
      this.#padRows        = Array.from(this.querySelectorAll('[data-bundle-pad-type]'));
      this.#totalEl        = this.querySelector('[data-bundle-total]');
      this.#learnMoreBtn   = this.querySelector('[data-bundle-learn-more]');

      const modalId = this.#config.modalId;
      this.#modal = modalId ? this.querySelector(`#${CSS.escape(modalId)}`) : null;

      // Move modal to <body> so it escapes any stacking-context ancestor
      if (this.#modal) document.body.appendChild(this.#modal);
    }

    // ── Variant cache ──────────────────────────────────────────────────────

    #cachePadVariants() {
      // Initial pass run in #syncFromVariant — nothing to pre-cache beyond
      // what connectedCallback already assigned from config JSON.
    }

    // ── Sync ───────────────────────────────────────────────────────────────

    /**
     * Central sync: called on init and on every variant change.
     * Updates the base price display, re-runs pad size matching, and
     * recalculates the running total. Accepts an optional pre-resolved
     * variant (passed from the mb:variant-change event); otherwise looks
     * the variant up by URL — used on init and on browser back/forward.
     */
    #syncFromVariant(variantIdStr, preResolvedVariant) {
      const found = preResolvedVariant
        || this.#allVariants.find((v) => String(v.id) === variantIdStr);
      const size = (found && (found.option1 || found.title)) || '';

      // Bundle subtotal needs the live variant price in cents. The event
      // payload carries it directly; on init/popstate the config JSON does.
      // Fallback to the existing #currentProductPrice if neither is present
      // (e.g. unavailable combination — keep the last known price).
      if (Number.isFinite(found?.price)) {
        this.#currentProductPrice = found.price;
      }

      // Mirror the price into the optional "Included" card (when shown).
      if (this.#basePriceEl && Number.isFinite(this.#currentProductPrice)) {
        this.#basePriceEl.textContent = ProductBundle.#formatMoney(this.#currentProductPrice);
      }

      if (size) {
        this.#runSizeMatch(size);
      } else {
        this.#recalcTotal();
      }
    }

    // ── Rug pad size matching ──────────────────────────────────────────────

    #runSizeMatch(size) {
      this.#currentVariantSize = size;
      this.#currentMohawkVariant = ProductBundle.#findMatchingVariant(this.#mohawkVariants, size);
      this.#currentStandardVariant = ProductBundle.#findMatchingVariant(this.#standardVariants, size);

      this.#updatePadRow(
        this.#mohawkRow, this.#mohawkPriceEl,
        this.#currentMohawkVariant, 'mohawk'
      );
      this.#updatePadRow(
        this.#standardRow, this.#standardPriceEl,
        this.#currentStandardVariant, 'standard'
      );

      const mohawkUnavail   = !this.#currentMohawkVariant?.available;
      const standardUnavail = !this.#currentStandardVariant?.available;
      if (mohawkUnavail && standardUnavail) {
        ProductBundle.#announce(this.#announceEl, 'No matching rug pad size available for this rug.');
      }

      this.#recalcTotal();
    }

    #updatePadRow(row, priceEl, variant, type) {
      if (!row) return;
      const available = Boolean(variant?.available);
      row.setAttribute('data-unavailable', available ? 'false' : 'true');

      if (priceEl) {
        priceEl.textContent = available ? '+' + ProductBundle.#formatMoney(variant.price) : '--';
        const label = available
          ? `Add ${type === 'mohawk' ? 'Mohawk Premium' : 'Standard'} rug pad for ${ProductBundle.#formatMoney(variant.price)}`
          : 'Rug pad size unavailable';
        priceEl.setAttribute('aria-label', label);
      }

      if (!available) {
        const inp = row.querySelector('input[type="checkbox"]');
        if (inp?.checked) inp.checked = false;
      }
    }

    // ── Total ──────────────────────────────────────────────────────────────

    #recalcTotal() {
      if (!this.#totalEl) return;

      let total = this.#currentProductPrice;

      const mohawkInp   = this.#mohawkRow?.querySelector('input[type="checkbox"]');
      const standardInp = this.#standardRow?.querySelector('input[type="checkbox"]');

      if (mohawkInp?.checked && this.#currentMohawkVariant?.available) {
        total += this.#currentMohawkVariant.price;
      } else if (standardInp?.checked && this.#currentStandardVariant?.available) {
        total += this.#currentStandardVariant.price;
      }

      for (const inp of this.#addonInputs) {
        if (inp.checked) {
          total += parseInt(inp.dataset.bundleAddonPrice, 10) || 0;
        }
      }

      const formatted = ProductBundle.#formatMoney(total);
      this.#totalEl.textContent = formatted;
      this.#totalEl.setAttribute('aria-label', 'Bundle total ' + formatted);

      // Scale-pulse on change
      this.#totalEl.removeAttribute('data-flash');
      void this.#totalEl.offsetWidth; // force reflow to restart animation
      this.#totalEl.setAttribute('data-flash', 'true');
      setTimeout(() => this.#totalEl?.removeAttribute('data-flash'), 260);
    }

    // ── Events ─────────────────────────────────────────────────────────────

    #bindEvents() {
      // Pad mutual exclusivity (radio-like)
      for (const row of this.#padRows) {
        const inp = row.querySelector('input[type="checkbox"]');
        if (!inp) continue;
        inp.addEventListener('change', () => {
          if (inp.checked) {
            for (const other of this.#padRows) {
              if (other === row) continue;
              const otherInp = other.querySelector('input[type="checkbox"]');
              if (otherInp?.checked) otherInp.checked = false;
            }
          }
          this.#recalcTotal();
        });
      }

      // Addon change
      for (const inp of this.#addonInputs) {
        inp.addEventListener('change', () => this.#recalcTotal());
      }

      // Variant change — modern-product.js dispatches `mb:variant-change` on
      // every selection with the resolved variant in event.detail. We re-sync
      // on every change so the bundle price stays live regardless of how the
      // user switches (radio click, swatch tap, browser nav).
      this.#onStateChange = (e) => {
        const variant = e?.detail?.variant ?? null;
        this.#syncFromVariant(variant ? String(variant.id) : '', variant);
      };

      // Browser back/forward — URL changes but mb:variant-change won't fire
      // unless the picker re-runs. Resolve from URL/cache as a fallback.
      this.#onPopState = () => {
        const size = this.#resolveCurrentSize();
        if (size && size !== this.#currentVariantSize) {
          this.#syncFromVariant(this.#currentVariantId());
        }
      };

      document.addEventListener('mb:variant-change', this.#onStateChange);
      window.addEventListener('popstate', this.#onPopState);

      // Modal
      if (this.#modal && this.#learnMoreBtn) {
        this.#learnMoreBtn.addEventListener('click', () => this.#openModal());

        const closeBtn  = this.#modal.querySelector('[data-bundle-modal-close]');
        const overlay   = this.#modal.querySelector('[data-bundle-modal-overlay]');
        if (closeBtn) closeBtn.addEventListener('click', () => this.#closeModal());
        if (overlay)  overlay.addEventListener('click',  () => this.#closeModal());

        this.#onKeydown = (e) => {
          if (e.key === 'Escape' && !this.#modal.hasAttribute('hidden')) {
            this.#closeModal();
          }
        };
        this.#onModalKeydown = (e) => this.#trapFocus(e);

        document.addEventListener('keydown', this.#onKeydown);
        this.#modal.addEventListener('keydown', this.#onModalKeydown);
      }
    }

    #unbindEvents() {
      if (this.#onStateChange) {
        document.removeEventListener('mb:variant-change', this.#onStateChange);
      }
      if (this.#onPopState) {
        window.removeEventListener('popstate', this.#onPopState);
      }
      if (this.#onKeydown) {
        document.removeEventListener('keydown', this.#onKeydown);
      }
      if (this.#modal && this.#onModalKeydown) {
        this.#modal.removeEventListener('keydown', this.#onModalKeydown);
      }
    }

    // ── Public API ─────────────────────────────────────────────────────────

    /**
     * Returns the currently-selected pad + addons as a Shopify /cart/add.js
     * `items` array. Empty array when nothing is checked.
     *
     * Mohawk and Standard pads are mutually exclusive (radio-style) so at
     * most one pad item is returned. Addons are independent checkboxes.
     *
     * IDs come from the live cache (#currentMohawkVariant / addonInputs
     * dataset) — kept in sync with the rug's selected size by
     * #syncFromVariant on every mb:variant-change. Consumed by
     * <product-form>'s submit path so cart additions are atomic.
     */
    getSelectedItems() {
      const items = [];

      const mohawkInp   = this.#mohawkRow?.querySelector('input[type="checkbox"]');
      const standardInp = this.#standardRow?.querySelector('input[type="checkbox"]');

      if (mohawkInp?.checked && this.#currentMohawkVariant?.available) {
        items.push({ id: this.#currentMohawkVariant.id, quantity: 1 });
      } else if (standardInp?.checked && this.#currentStandardVariant?.available) {
        items.push({ id: this.#currentStandardVariant.id, quantity: 1 });
      }

      for (const inp of this.#addonInputs) {
        if (inp.checked) {
          const id = parseInt(inp.dataset.bundleAddonId, 10);
          if (Number.isFinite(id)) items.push({ id, quantity: 1 });
        }
      }

      return items;
    }

    // ── Modal ──────────────────────────────────────────────────────────────

    #openModal() {
      this.#lastFocusTrigger = document.activeElement;
      this.#modal.removeAttribute('hidden');
      this.#modal.setAttribute('data-state', 'open');
      document.body.style.overflow = 'hidden';
      const focusable = this.#getFocusable();
      focusable[0]?.focus();
    }

    #closeModal() {
      this.#modal.setAttribute('hidden', '');
      this.#modal.removeAttribute('data-state');
      document.body.style.overflow = '';
      this.#lastFocusTrigger?.focus();
    }

    #trapFocus(e) {
      if (e.key !== 'Tab') return;
      const focusable = this.#getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    }

    #getFocusable() {
      return Array.from(
        this.#modal.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled])'
        )
      ).filter((el) => !el.closest('[hidden]'));
    }

    // ── Helpers ────────────────────────────────────────────────────────────

    #resolveCurrentSize() {
      const variantId = this.#currentVariantId();
      const found = this.#allVariants.find((v) => String(v.id) === variantId);
      return (found && (found.option1 || found.title)) || '';
    }
  }

  customElements.define('product-bundle', ProductBundle);
}
